import * as _ from 'lodash';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UpdateServiceInterface } from '../interfaces/update-service.interface';
import { ModelHelper } from './model-helper';

/**
 * Basis model for all standard models
 */
export abstract class Model {
	// =======================================================================================================================================
	// Static properties
	// =======================================================================================================================================

	/**
	 * Service to save the object
	 */
	protected static _service: UpdateServiceInterface;

	// =======================================================================================================================================
	// Properties
	// =======================================================================================================================================

	/**
	 * UID of the instance
	 */
	public uid: string;

	// =======================================================================================================================================
	// Static methods with offered implementation
	// =======================================================================================================================================

	/**
	 * Init (main) service
	 */
	public static init(service: UpdateServiceInterface): void {
		this._service = service;
	}

	/**
	 * Static method of map
	 *
	 * @param data - Data object to fill the specific model
	 * @param item - Existing instance that is to be used instead of a new one
	 */
	public static map<T extends Model>(this: new (...args: any[]) => T, data: any, item: T = new this()): T {
		return item.map(data);
	}

	/**
	 * Static method of getDataForApi
	 *
	 * @param data - Data object to convert for API
	 * @param item - Existing instance that is to be used instead of a new one
	 */
	public static getDataForApi<T extends Model>(this: new (...args: any[]) => T, data: any, item: T = new this()): any {
		return item.getDataForApi(data);
	}

	// =======================================================================================================================================
	// Abstract methods to be implemented
	// =======================================================================================================================================

	/**
	 * Process data and save the results into properties of the model
	 *
	 * @param data - Data object to fill the specific model
	 * @return Specific model
	 */
	public abstract map(data: object): this;

	// =======================================================================================================================================
	// Methods with offered implementation
	// =======================================================================================================================================

	/**
	 * Get data for API
	 *
	 * Overwrite this method if the data format of the model does not match the input for update function in the corresponding API service
	 *
	 * @param data
	 */
	public getDataForApi(data: any = this): any {
		// Clone data
		let apiData: any = (<any>this.constructor).map(JSON.parse(JSON.stringify(data)));

		// Process properties
		for (const [key, value] of Object.entries(apiData)) {
			// Check for own getDataForApi method
			if (value && (<any>value).getDataForApi) {
				apiData[key] = (<any>value).getDataForApi();
			} else {
				apiData[key] = JSON.parse(JSON.stringify(value));
			}
		}

		// Reduce api data to original properties of data
		apiData = ModelHelper.intersection(JSON.parse(JSON.stringify(data)), apiData);

		// Return data for api
		return apiData;
	}

	/**
	 * Change Detection of two Objects with deep compare of array
	 *
	 * @param data - Data to be mapped
	 * @param compData - Comparison data, if these are specified, only the difference will be stored
	 * @return Mapped data
	 */
	public mapDataWithoutAffectingInstance(data: any, compData?: any): object {
		// Process array
		if (Array.isArray(data)) {
			// Easy compare
			if (!Array.isArray(compData)) {
				return data;
			}

			// Empty data array
			if (!data.length) {
				return !compData || compData.length ? [] : undefined;

				// Different length
			} else if (data.length !== compData.length) {
				return data;
			}

			// Deep compare
			let changeDetected = false;
			for (const key of Object.keys(data)) {
				const compItem = this.mapDataWithoutAffectingInstance(data[key], compData[key]);

				// If not undefined and not an empty object, there are changes
				if (!(compItem === undefined || (typeof compItem === 'object' && !Object.keys(compItem).length))) {
					changeDetected = true;
				}
			}

			if (changeDetected) {
				return data;
			}

			// Return mapped array
			return undefined;
		}

		// Process primitive
		if (!data || typeof data !== 'object') {
			if (data === compData) {
				return undefined;
			} else {
				return data;
			}
		}

		// Process object
		const mappedChanges: object = {};

		// Create a new data instance so that the call by reference does not lead to unwanted side effects
		const mappedData = _.cloneDeep(data);

		// Include only changed properties
		for (const prop of Object.getOwnPropertyNames(data)) {
			if (
				// No undefined property
				typeof mappedData[prop] !== undefined &&
				// No function
				typeof mappedData[prop] !== 'function' &&
				// No empty object
				!(
					mappedData[prop] &&
					!Array.isArray(mappedData[prop]) &&
					typeof mappedData[prop] === 'object' &&
					Object.keys(mappedData[prop]).length === 0
				) &&
				// Compare data is null
				(compData === null ||
					compData === undefined ||
					// Property was not undefined is now null
					(!(compData[prop] === undefined && mappedData[prop] === null) &&
						// Compare property and current property are unequal
						JSON.stringify(mappedData[prop]) !== JSON.stringify(compData[prop])))
			) {
				mappedChanges[prop] = this.mapDataWithoutAffectingInstance(mappedData[prop], compData ? compData[prop] : undefined);
			}
		}

		// Return mapped changes (for saving)
		return Object.keys(mappedChanges).length ? mappedChanges : undefined;
	}

	/**
	 * Save included models and return the rest
	 *
	 * @param data
	 * @param compData
	 */
	public saveIncludedModels(data: any, compData: any = {}): Observable<any> {
		return new Observable<object>((subscriber) => {
			// Check data
			if (!data || typeof data !== 'object') {
				subscriber.next(data);
				subscriber.complete();

				// Data is an empty array
			} else if (Array.isArray(data) && data.length === 0 && (!Array.isArray(compData) || compData.length !== 0)) {
				subscriber.next(data);
				subscriber.complete();

				// Data is an object and has its own save method
			} else if (typeof data['save'] === 'function') {
				(<Model>data).save(data, compData).subscribe(
					() => {
						subscriber.next(undefined);
						subscriber.complete();
					},
					(err) => {
						subscriber.error(err);
					}
				);

				// Process objects (without own save method) / non empty arrays
			} else {
				// Multiple subscriptions
				const subscriptions = [];

				// Process values
				for (const key of Object.keys(data)) {
					const comp = compData ? compData[key] : undefined;
					subscriptions.push(
						this.saveIncludedModels(data[key], comp).pipe(
							map((response) => {
								if (
									// Undefined data
									response === undefined ||
									// Empty objects are not allowed in GraphQL
									(response && !Array.isArray(response) && typeof response === 'object' && Object.keys(response).length === 0)
								) {
									delete data[key];
								} else {
									data[key] = response;
								}
							})
						)
					);
				}

				// All subscriptions done
				if (subscriptions.length > 0) {
					forkJoin(subscriptions).subscribe(
						() => {
							if (Object.keys(data).length === 0) {
								subscriber.next(undefined);
							} else {
								subscriber.next(data);
							}
							subscriber.complete();
						},
						(err) => {
							subscriber.error(err);
						}
					);
				} else {
					if (Object.keys(data).length === 0) {
						subscriber.next(undefined);
					} else {
						subscriber.next(data);
					}
					subscriber.complete();
				}
			}
		});
	}

	/**
	 * Save data and update model with API data
	 *
	 * @param data - Data to be saved
	 * @param compData - Comparison data, if these are specified, only the difference will be stored
	 * @param params - Additional params for update method in service
	 * @param schema - GraphQL Schema
	 */
	public save(data: object = this, compData?: object, schema?: any, ...params: any[]): Observable<this> {
		return new Observable<any>((subscriber) => {
			// Map changes
			data = this.mapDataWithoutAffectingInstance(data, compData);

			// Save included models
			this.saveIncludedModels(data, compData).subscribe((unsavedData) => {
				// Prepare data for API
				if (typeof this.getDataForApi === 'function') {
					unsavedData = this.getDataForApi(unsavedData);
				}

				// If no unsavedData for API exists
				if (!unsavedData || (typeof unsavedData === 'object' && !Object.entries(unsavedData).length)) {
					subscriber.next(this);
					subscriber.complete();
					return;
				}

				// Check service
				if (!(<any>this.constructor)._service) {
					return subscriber.error('Missing service in ' + (<any>this.constructor).name + '. Please integrate service via InitService');
				}

				// Save changes
				(<any>this.constructor)._service.update(this.uid, unsavedData, schema, ...params).subscribe(
					(response) => {
						// Return model from response
						subscriber.next(response);
						subscriber.complete();
					},
					(err) => {
						// Return error
						subscriber.error(err);
					}
				);
			});
		});
	}
}
